import { all, call, put, select, takeEvery } from 'redux-saga/effects'

import { scheduleApi } from '../../api/scheduleApi'
import {
    SCHEDULE_CHECK_OCCUPANCY,
    SCHEDULE_DELETE_LESSON,
    SCHEDULE_LOAD,
    SCHEDULE_LOAD_FOR_CLASSES,
    SCHEDULE_SAVE_LESSON,
} from '../actions/schedule'
import { authSelector } from '../slices/auth'
import { scheduleActions } from '../slices/schedule'

function normalizeScheduleResponse(data) {
    const lessons = Array.isArray(data?.lessons) ? data.lessons : [data?.lessons].filter(Boolean)

    return {
        items: lessons.filter(Boolean),
        total: data?.total ?? lessons.length ?? 0,
    }
}

function* scheduleLoadSaga(action) {
    const isAuthorized = yield select(authSelector.isAuthorized)

    if (!isAuthorized) return

    const filters = action.payload

    if (filters.viewMode === 'group' && !filters.selectedGroupId) return
    if (filters.viewMode === 'teacher' && !filters.selectedTeacherId) return

    try {
        yield put(scheduleActions.setScheduleLoading(true))

        const data = yield call(scheduleApi.getSemester, filters)
        const normalized = normalizeScheduleResponse(data)

        yield put(scheduleActions.setScheduleData(normalized))
    } catch (error) {
        console.error('SCHEDULE ERROR:', error)
        yield put(scheduleActions.setScheduleError(
            error instanceof Error ? error.message : 'Ошибка загрузки расписания'
        ))
    } finally {
        yield put(scheduleActions.setScheduleLoading(false))
    }
}

function* scheduleLoadForClassesSaga(action) {
    const isAuthorized = yield select(authSelector.isAuthorized)

    if (!isAuthorized) return

    const classIds = action.payload

    if (!classIds) return

    try {
        yield put(scheduleActions.setScheduleLoading(true))

        const data = yield call(scheduleApi.getForClasses, classIds)
        const items = Array.isArray(data) ? data : []

        yield put(scheduleActions.setScheduleData({ items, total: 0 }))
    } catch (error) {
        console.error('SCHEDULE FOR CLASSES ERROR:', error)
        yield put(scheduleActions.setScheduleError(
            error instanceof Error ? error.message : 'Ошибка загрузки занятий по алерту'
        ))
    } finally {
        yield put(scheduleActions.setScheduleLoading(false))
    }
}

function* scheduleSaveLessonSaga(action) {
    const { lessonId, payload, reloadFilters, reloadClassIds } = action.payload

    try {
        yield put(scheduleActions.setSaveLoading(true))

        yield call(scheduleApi.saveLesson, { lessonId, payload })

        if (reloadClassIds) {
            yield call(scheduleLoadForClassesSaga, { payload: reloadClassIds })
        } else if (reloadFilters) {
            yield call(scheduleLoadSaga, { payload: reloadFilters })
        }
    } catch (error) {
        console.error('SCHEDULE SAVE ERROR:', error)
        alert('Ошибка сохранения')
    } finally {
        yield put(scheduleActions.setSaveLoading(false))
    }
}

function* scheduleDeleteLessonSaga(action) {
    const { lessonId, reloadFilters, reloadClassIds } = action.payload

    try {
        yield put(scheduleActions.setDeleteLoading(true))

        yield call(scheduleApi.deleteLesson, lessonId)

        if (reloadClassIds) {
            yield call(scheduleLoadForClassesSaga, { payload: reloadClassIds })
        } else if (reloadFilters) {
            yield call(scheduleLoadSaga, { payload: reloadFilters })
        }
    } catch (error) {
        console.error('SCHEDULE DELETE ERROR:', error)
        alert('Ошибка удаления')
    } finally {
        yield put(scheduleActions.setDeleteLoading(false))
    }
}

function* scheduleCheckOccupancySaga(action) {
    const payload = action.payload

    if (!payload) {
        yield put(scheduleActions.resetOccupancy())
        return
    }

    const { day, teacherId, groupIds, roomId } = payload

    if (!day || !teacherId || !groupIds?.length || !roomId) {
        yield put(scheduleActions.resetOccupancy())
        return
    }

    try {
        yield put(scheduleActions.setOccupancyLoading(true))

        const data = yield call(scheduleApi.checkOccupancy, {
            day,
            teacherId,
            groupIds: groupIds.join(','),
            roomId,
        })

        yield put(scheduleActions.setOccupancyData(Array.isArray(data) ? data : [data]))
    } catch (error) {
        console.error('OCCUPANCY ERROR:', error)
        yield put(scheduleActions.setOccupancyData([]))
    } finally {
        yield put(scheduleActions.setOccupancyLoading(false))
    }
}

function* scheduleLoadWatch() {
    yield takeEvery(SCHEDULE_LOAD, scheduleLoadSaga)
}

function* scheduleLoadForClassesWatch() {
    yield takeEvery(SCHEDULE_LOAD_FOR_CLASSES, scheduleLoadForClassesSaga)
}

function* scheduleSaveLessonWatch() {
    yield takeEvery(SCHEDULE_SAVE_LESSON, scheduleSaveLessonSaga)
}

function* scheduleDeleteLessonWatch() {
    yield takeEvery(SCHEDULE_DELETE_LESSON, scheduleDeleteLessonSaga)
}

function* scheduleCheckOccupancyWatch() {
    yield takeEvery(SCHEDULE_CHECK_OCCUPANCY, scheduleCheckOccupancySaga)
}

export default function* scheduleRootSaga() {
    yield all([
        scheduleLoadWatch(),
        scheduleLoadForClassesWatch(),
        scheduleSaveLessonWatch(),
        scheduleDeleteLessonWatch(),
        scheduleCheckOccupancyWatch(),
    ])
}
